import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../utils/api";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import AdminAgentStudentModal from "./AdminAgentStudentModal";

export default function AdminAgentDetailPage() {
  const { agent_id } = useParams();

  const [agent, setAgent] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const loadAgent = async () => {
    const res = await api.get(`/admin/agents/${agent_id}`);
    const data = res.data;

    const earned = Number(data.earned_total || 0);
    const paid = Number(data.paid_total || 0);

    setAgent({
      ...data,
      earned_total: earned,
      paid_total: paid,
      available_balance: Math.max(earned - paid, 0),
    });
  };

  useEffect(() => {
    loadAgent();
    api.get(`/admin/agents/${agent_id}/students`).then((res) => {
      setStudents(res.data.students || []);
    });
  }, [agent_id]);

  const verifyBank = async () => {
    if (!window.confirm("Verify this agent's bank account?")) return;
    try {
      setVerifying(true);
      await api.post(`/admin/agents/${agent_id}/verify-bank`);
      await loadAgent();
    } finally {
      setVerifying(false);
    }
  };

  if (!agent) return null;

  return (
    <Box p={3}>
      {/* ================= HEADER ================= */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700}>
          {agent.agent_name}
        </Typography>
        <Typography color="text.secondary">{agent.email}</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* ================= FINANCE SUMMARY ================= */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6">Finance Summary</Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography>
              <strong>Total Students:</strong> {agent.total_students}
            </Typography>
            <Typography mt={1}>
              <strong>Earned Total:</strong> ₦{agent.earned_total.toLocaleString()}
            </Typography>
            <Typography mt={1}>
              <strong>Paid Total:</strong> ₦{agent.paid_total.toLocaleString()}
            </Typography>
            <Typography mt={1} color="primary">
              <strong>Available Commission:</strong> ₦
              {agent.available_balance.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>

        {/* ================= BANK INFO ================= */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6">Bank Account Information</Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography>
              <strong>Account Name:</strong> {agent.account_name || "Not provided"}
            </Typography>
            <Typography mt={1}>
              <strong>Bank Name:</strong> {agent.bank_name || "Not provided"}
            </Typography>
            <Typography mt={1}>
              <strong>Account Number:</strong> {agent.account_number || "Not provided"}
            </Typography>

            <Stack direction="row" spacing={2} mt={2} alignItems="center">
              <Chip
                label={agent.payout_verified ? "Verified" : "Not Verified"}
                color={agent.payout_verified ? "success" : "warning"}
              />

              {!agent.payout_verified && (
                <Button
                  variant="contained"
                  onClick={verifyBank}
                  disabled={verifying}
                >
                  {verifying ? "Verifying..." : "Verify Bank"}
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* ================= STUDENT TABLE ================= */}
      <Box mt={4}>
        <Typography variant="h6">Referred Students</Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Total Paid</TableCell>
                <TableCell>Agent Earned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((s) => (
                <TableRow
                  key={s.student_id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedStudent(s)}
                >
                  <TableCell>{s.student_name}</TableCell>
                  <TableCell>{s.email}</TableCell>
                  <TableCell>₦{Number(s.total_paid).toLocaleString()}</TableCell>
                  <TableCell>₦{Number(s.agent_earned).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {selectedStudent && (
        <AdminAgentStudentModal
          agentId={agent_id}
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </Box>
  );
}
